import {
	forwardRef,
	useEffect,
	useImperativeHandle,
	useRef,
	useState,
} from "react";
import styled from "styled-components";

const Container = styled.div`
	position: relative;
	max-width: ${(props) => (props.isLimited ? "300px" : "none")};
	min-width: ${(props) => (props.isLimited ? "200px" : "none")};
	opacity: ${(props) => (props.disabled ? "0.5" : "1")};
	cursor: ${(props) => (props.disabled ? "not-allowed" : "auto")};
	margin: 12px 0;
`;

const BoxContainer = styled.div`
	position: relative;
	width: 100%;
	height: ${(props) => (props.isInput ? "56px" : "auto")};
	display: flex;
	padding-left: ${(props) => (props.hasIcon ? "12px" : "16px")};
	padding-right: ${(props) => (props.hasIcon ? "12px" : "16px")};
	border: ${(props) => (props.focus ? "2px" : "1px")} solid
		${(props) =>
			props.isFocused
				? "var(--color-primary)"
				: props.isError
				? "var(--color-error)"
				: "var(--color-outline)"};
	border-radius: 8px;
	cursor: text;
	transition: 0.3s;

	&:hover {
		opacity: 1;
	}
`;

const Label = styled.label`
	position: absolute;
	top: ${(props) => (props.isFocused ? "-9px" : "calc(50% - 9px)")};
	padding: 0 ${(props) => (props.isFocused ? "6px" : "0px")};
	left: ${(props) => (props.isFocused ? "10px" : "16px")};
	background: var(--color-background);
	color: ${(props) =>
		props.isFocused
			? "var(--color-primary)"
			: props.isError
			? "var(--color-error)"
			: "var(--color-on-surface-variant)"};
	font-style: var(
		${(props) =>
			props.isFocused
				? "--md-sys-typescale-body-small-font-family-style"
				: "--md-sys-typescale-body-large-font-family-style"}
	);
	font-weight: var(
		${(props) =>
			props.isFocused
				? "--md-sys-typescale-body-small-font-weight"
				: "--md-sys-typescale-body-large-font-weight"}
	);
	font-size: var(
		${(props) =>
			props.isFocused
				? "--md-sys-typescale-body-small-font-size"
				: "--md-sys-typescale-body-large-font-size"}
	);
	letter-spacing: var(
		${(props) =>
			props.isFocused
				? "--md-sys-typescale-body-small-tracking"
				: "--md-sys-typescale-body-large-tracking"}
	);
	line-height: var(
		${(props) =>
			props.isFocused
				? "--md-sys-typescale-body-small-height"
				: "--md-sys-typescale-body-large-height"}
	);
	text-transform: var(
		${(props) =>
			props.isFocused
				? "--md-sys-typescale-body-small-text-transform"
				: "--md-sys-typescale-body-large-text-transform"}
	);
	text-decoration: var(
		${(props) =>
			props.isFocused
				? "--md-sys-typescale-body-small-text-decoration"
				: "--md-sys-typescale-body-large-text-decoration"}
	);
	transition: 0.3s;
`;

const InputLayout = styled.div`
	display: flex;
	width: 100%;
	flex-direction: row;
	gap: 16px;
	align-items: center;
`;

const TrailingContent = styled.div`
	position: relative;
	height: 24px;
	opacity: ${(props) => (props.isFocus ? "1" : "0")};
	transition: 0.1s;

	&::before {
		content: "";
		position: absolute;
		top: 50%;
		left: 50%;
		width: 0px;
		height: 0px;
		border-radius: 50%;
		background: rgba(from var(--color-on-surface-variant) r g b / 0%);
		transition: 0.3s;
	}

	&:hover::before {
		top: -6px;
		left: -6px;
		width: 36px;
		height: 36px;
		background: rgba(from var(--color-on-surface-variant) r g b / 10%);
	}
`;

const TrailingIcon = styled.img`
	flex: 1 1;
	width: 24px;
	height: 24px;
	object-fit: contain;
`;

const Input = styled.input`
	min-width: 0;
	flex: 1 1 auto;
	outline: none;
	background: none;
	border: none;
	cursor: text;
	color: var(
		${(props) =>
			props.isError ? "--color-error" : "--color-on-background"}
	);
	caret-color: var(
		${(props) =>
			props.isError ? "--color-error" : "--color-on-background"}
	);
	font-style: var(--md-sys-typescale-body-large-font-family-style);
	font-weight: var(--md-sys-typescale-body-large-font-weight);
	font-size: var(--md-sys-typescale-body-large-font-size);
	letter-spacing: var(--md-sys-typescale-body-large-tracking);
	line-height: var(--md-sys-typescale-body-large-height);
	text-transform: var(--md-sys-typescale-body-large-text-transform);
	text-decoration: var(--md-sys-typescale-body-large-text-decoration);

	@media (prefers-color-scheme: dark) {
		color-scheme: dark;
	}
`;

const TextArea = styled.textarea`
	min-width: 0;
	flex: 1 1 auto;
	outline: none;
	background: none;
	border: none;
	padding: 12px 0px;
	resize: vertical;
	cursor: text;
	color: var(
		${(props) =>
			props.isError ? "--color-error" : "--color-on-background"}
	);
	caret-color: var(
		${(props) =>
			props.isError ? "--color-error" : "--color-on-background"}
	);
	font-style: var(--md-sys-typescale-body-large-font-family-style);
	font-weight: var(--md-sys-typescale-body-large-font-weight);
	font-size: var(--md-sys-typescale-body-large-font-size);
	letter-spacing: var(--md-sys-typescale-body-large-tracking);
	line-height: var(--md-sys-typescale-body-large-height);
	text-transform: var(--md-sys-typescale-body-large-text-transform);
	text-decoration: var(--md-sys-typescale-body-large-text-decoration);

	@media (prefers-color-scheme: dark) {
		color-scheme: dark;
	}
`;

const SelectArrow = styled.span`
	cursor: pointer;
	transform: rotate(${(props) => (props.isOpen ? "180deg" : "0deg")});
	color: var(
		${(props) => (props.isOpen ? "--color-primary" : "--color-on-surface")}
	);
	transition: 0.3s;
`;

const OptionContainer = styled.div`
	position: absolute;
	top: 56px;
	left: 0;
	overflow: hidden;
	background-color: var(--color-surface-container);
	display: flex;
	flex-direction: column;
	width: 100%;
	height: ${(props) => (props.isOpen ? props.openHeight : "0")}px;
	border-radius: 8px;
	z-index: 2;
	transition: 0.3s;
`;

const Option = styled.div`
	background-color: var(--color-surface-container);
	color: var(--color-on-surface);
	padding: 6px 12px;
	height: 36px;
	cursor: pointer;
	transition: 0.3s;

	&:hover {
		background-color: var(--color-surface-container-highest);
	}
`;

const Suffix = styled.span`
	color: var(--color-on-surface-variant);
	font-style: var(--md-sys-typescale-body-large-font-family-style);
	font-weight: var(--md-sys-typescale-body-large-font-weight);
	font-size: var(--md-sys-typescale-body-large-font-size);
	letter-spacing: var(--md-sys-typescale-body-large-tracking);
	line-height: var(--md-sys-typescale-body-large-height);
	text-transform: var(--md-sys-typescale-body-large-text-transform);
	text-decoration: var(--md-sys-typescale-body-large-text-decoration);
	opacity: ${(props) => (props.isFocus ? "1" : "0")};
	transition: 0.1s;
`;

const SupporterText = styled.span`
	padding: 0 16px;
	color: ${(props) =>
		props.isError
			? "var(--color-error)"
			: "var(--color-on-surface-variant)"};
	font-style: var(--md-sys-typescale-body-small-font-family-style);
	font-weight: var(--md-sys-typescale-body-small-font-weight);
	font-size: var(--md-sys-typescale-body-small-font-size);
	letter-spacing: var(--md-sys-typescale-body-small-tracking);
	line-height: var(--md-sys-typescale-body-small-height);
	text-transform: var(--md-sys-typescale-body-small-text-transform);
	text-decoration: var(--md-sys-typescale-body-small-text-decoration);
`;

const EyeSvg = styled.svg`
	position: relative;
	min-width: 24px;
	width: 24px;
	height: 24px;
	cursor: pointer;
	fill: var(--color-on-surface-variant);
	transition: all 0.3s ease;
`;

function PasswordEye({ showPassword, togglePasswordVisibility }) {
	return (
		<EyeSvg
			xmlns="http://www.w3.org/2000/svg"
			height="24px"
			viewBox="0 -960 960 960"
			width="24px"
			onClick={togglePasswordVisibility}
		>
			<path
				d={
					showPassword
						? "M480-320q75 0 127.5-52.5T660-500q0-75-52.5-127.5T480-680q-75 0-127.5 52.5T300-500q0 75 52.5 127.5T480-320Zm0-72q-45 0-76.5-31.5T372-500q0-45 31.5-76.5T480-608q45 0 76.5 31.5T588-500q0 45-31.5 76.5T480-392Zm0 192q-134 0-244.5-72T61-462q-5-9-7.5-18.5T51-500q0-10 2.5-19.5T61-538q64-118 174.5-190T480-800q134 0 244.5 72T899-538q5 9 7.5 18.5T909-500q0 10-2.5 19.5T899-462q-64 118-174.5 190T480-200Z"
						: "M764-84 624-222q-35 11-71 16.5t-73 5.5q-134 0-245-72T61-462q-5-9-7.5-18.5T51-500q0-10 2.5-19.5T61-538q22-39 47-76t58-66l-83-84q-11-11-11-27.5T84-820q11-11 28-11t28 11l680 680q11 11 11.5 27.5T820-84q-11 11-28 11t-28-11ZM480-320q11 0 21-1t20-4L305-541q-3 10-4 20t-1 21q0 75 52.5 127.5T480-320Zm0-480q134 0 245.5 72.5T900-537q5 8 7.5 17.5T910-500q0 10-2 19.5t-7 17.5q-19 37-42.5 70T806-331q-14 14-33 13t-33-15l-80-80q-7-7-9-16.5t1-19.5q4-13 6-25t2-26q0-75-52.5-127.5T480-680q-14 0-26 2t-25 6q-10 3-20 1t-17-9l-33-33q-19-19-12.5-44t31.5-32q25-5 50.5-8t51.5-3Zm79 226q11 13 18.5 28.5T587-513q1 8-6 11t-13-3l-82-82q-6-6-2.5-13t11.5-7q19 2 35 10.5t29 22.5Z"
				}
			/>
		</EyeSvg>
	);
}

export const CorzTextField = forwardRef(
	(
		{
			id,
			limited = true,
			required = false,
			label,
			type = "text",
			value,
			leadingIconSrc,
			trailingIconSrc,
			trailingIconClick,
			supporterText,
			suffixText,
			onValueChange = () => { },
			disabled = false,
		},
		ref
	) => {
		const [focused, setFocused] = useState(
			value != null || type === "date"
		);
		const [nowFocused, setNowFocused] = useState(false);
		const [showPassword, setShowPassword] = useState(false);
		const [inputType, setInputType] = useState(type);
		const [hasError, setHasError] = useState(false);
		const [supporterTxt, setSupporterTxt] = useState(supporterText);

		const inputRef = useRef();

		const handleOnFocusEvent = () => {
			setFocused(true);
			setNowFocused(true);
			updateError(false); // remove error on focus
		};

		const handleOnBlurEvent = () => {
			setNowFocused(false);
			if (inputRef.current.value === "" && inputType !== "date") {
				setFocused(false);
			}
		};

		const togglePasswordVisibility = () => {
			setShowPassword(!showPassword);
			setInputType(inputType === "text" ? "password" : "text");
		};

		const updateError = (error, errorText = supporterText) => {
			setHasError(error);
			setSupporterTxt(errorText);
		};

		const getValue = () => inputRef.current.value || "";

		const valueChangeHandler = (e) => {
			onValueChange(ref, e.target.value);
		};

		useImperativeHandle(ref, () => ({
			getValue,
			updateError,
		}));

		return (
			<Container id={id} isLimited={limited} disabled={disabled}>
				<BoxContainer
					isInput={true}
					isError={hasError}
					isFocused={focused}
					hasIcon={leadingIconSrc}
					focus={nowFocused}
					onClick={() => inputRef.current.focus()}
				>
					<Label
						htmlFor={`input-${id}`}
						isFocused={focused}
						isError={hasError}
					>
						{label}
					</Label>
					<InputLayout>
						{leadingIconSrc && (
							<TrailingIcon src={leadingIconSrc} />
						)}
						<Input
							ref={inputRef}
							id={`input-${id}`}
							name={id}
							type={inputType}
							defaultValue={value}
							required={required}
							disabled={disabled}
							isError={hasError}
							onFocus={handleOnFocusEvent}
							onBlur={handleOnBlurEvent}
							onChange={valueChangeHandler}
						/>
						{suffixText && (
							<Suffix isFocus={focused}>{suffixText}</Suffix>
						)}
						{(type === "password" || trailingIconSrc) && (
							<TrailingContent isFocus={focused}>
								{type === "password" ? (
									<PasswordEye
										showPassword={showPassword}
										togglePasswordVisibility={
											togglePasswordVisibility
										}
									/>
								) : (
									trailingIconSrc && (
										<TrailingIcon
											src={trailingIconSrc}
											onClick={() => trailingIconClick}
										/>
									)
								)}
							</TrailingContent>
						)}
					</InputLayout>
				</BoxContainer>
				{supporterTxt && (
					<SupporterText isError={hasError}>
						{supporterTxt}
					</SupporterText>
				)}
			</Container>
		);
	}
);

export const CorzTextArea = forwardRef(
	(
		{
			id,
			limited = true,
			required = false,
			label,
			value,
			supporterText,
			suffixText,
			onValueChange = () => { },
			disabled = false,
		},
		ref
	) => {
		const [focused, setFocused] = useState(false);
		const [nowFocused, setNowFocused] = useState(false);
		const [hasError, setHasError] = useState(false);
		const [supporterTxt, setSupporterTxt] = useState(supporterText);

		const inputRef = useRef();

		const handleOnFocusEvent = () => {
			setFocused(true);
			setNowFocused(true);
			updateError(false); // remove error on focus
		};

		const handleOnBlurEvent = () => {
			setNowFocused(false);
			if (inputRef.current.value === "") {
				setFocused(false);
			}
		};

		const updateError = (error, errorText = supporterText) => {
			setHasError(error);
			setSupporterTxt(errorText);
		};

		const getValue = () => inputRef.current.value || "";

		const valueChangeHandler = (e) => {
			onValueChange(ref, e.target.value);
		};

		useImperativeHandle(ref, () => ({
			getValue,
			updateError,
		}));

		return (
			<Container id={id} isLimited={limited} disabled={disabled}>
				<BoxContainer
					isInput={false}
					isError={hasError}
					isFocused={focused}
					focus={nowFocused}
					onClick={() => inputRef.current.focus()}
				>
					<Label
						htmlFor={`textarea-${id}`}
						isFocused={focused}
						isError={hasError}
					>
						{label}
					</Label>
					<InputLayout>
						<TextArea
							ref={inputRef}
							id={`textarea-${id}`}
							name={id}
							defaultValue={value}
							required={required}
							isError={hasError}
							disabled={disabled}
							onFocus={handleOnFocusEvent}
							onBlur={handleOnBlurEvent}
							onChange={valueChangeHandler}
						/>
						{suffixText && (
							<Suffix isFocus={focused}>{suffixText}</Suffix>
						)}
					</InputLayout>
				</BoxContainer>
				{supporterTxt && (
					<SupporterText isError={hasError}>
						{supporterTxt}
					</SupporterText>
				)}
			</Container>
		);
	}
);

export const CorzSelect = forwardRef(
	(
		{
			id,
			limited = true,
			required = false,
			label,
			value,
			supporterText,
			suffixText,
			entryList = [],
			onSelectChange = () => {},
		},
		ref
	) => {
		const [focused, setFocused] = useState(false);
		const [nowFocused, setNowFocused] = useState(false);
		const [hasError, setHasError] = useState(false);
		const [supporterTxt, setSupporterTxt] = useState(supporterText);
		const [entryData, setEntryData] = useState([]);
		const [nowSelected, setNowSelected] = useState(false);

		const inputRef = useRef();

		useEffect(() => {
			console.log(entryList);

			setEntryData(entryList);
		}, [entryList]);

		const handleOnFocusEvent = () => {
			if (nowSelected) {
				setNowSelected(false);
				return;
			}

			setFocused(true);
			setNowFocused(true);
			updateError(false); // remove error on focus
		};

		const handleOnBlurEvent = () => {
			setNowFocused(false);
			if (inputRef.current.value === "") {
				setFocused(false);
			}
		};

		const updateError = (error, errorText = supporterText) => {
			setHasError(error);
			setSupporterTxt(errorText);
		};

		const getValue = () => inputRef.current.value;

		const valueChangeHandler = (entry) => {
			onSelectChange(ref, entry.key);
			setNowSelected(true);
			inputRef.current.value = entry.value;
		};

		useImperativeHandle(ref, () => ({
			getValue,
			updateError,
			setEntryList: (entryList) => setEntryData(entryList),
		}));

		return (
			<Container id={id} isLimited={limited}>
				<BoxContainer
					style={{ cursor: "pointer" }}
					isInput={true}
					isError={hasError}
					isFocused={focused}
					focus={nowFocused}
					onClick={() => inputRef.current.focus()}
				>
					<Label
						htmlFor={`select-${id}`}
						isFocused={focused}
						isError={hasError}
					>
						{label}
					</Label>
					<InputLayout>
						<Input
							readOnly
							ref={inputRef}
							id={`select-${id}`}
							defaultValue={value}
							type="none"
							required={required}
							isError={hasError}
							style={{ pointerEvents: "none", cursor: "pointer" }}
							onFocus={handleOnFocusEvent}
							onBlur={handleOnBlurEvent}
							onChange={valueChangeHandler}
						/>
						<SelectArrow
							className="material-symbol fill"
							isOpen={nowFocused}
						>
							keyboard_arrow_down
						</SelectArrow>
						{suffixText && (
							<Suffix isFocus={nowFocused}>{suffixText}</Suffix>
						)}
					</InputLayout>
					<OptionContainer
						isOpen={nowFocused}
						openHeight={entryData.length * 36}
					>
						{entryData.map((item, key) => (
							<Option
								key={key}
								value={item.key}
								onMouseDown={() => valueChangeHandler(item)}
							>
								{item.value}
							</Option>
						))}
					</OptionContainer>
				</BoxContainer>
				{supporterTxt && (
					<SupporterText isError={hasError}>
						{supporterTxt}
					</SupporterText>
				)}
			</Container>
		);
	}
);
